import React, { useState } from 'react'

import moment from 'moment'

import { ButtonWithLoading } from 'app/components/design-system-components'
import * as Display from 'app/components/display/display'
import DisclosureQuestions from 'app/pages/teams/onlist/components/Disclosures'
import { PropertyDisclosureHeader } from 'app/pages/teams/onlist/components/Disclosures/PropertyDisclosureHeader'
import { getDisclosureLatestVersion } from 'app/pages/teams/onlist/components/Disclosures/questions'
import {
  getInitialDisclosureErrors,
  validateDisclosureAnswers,
} from 'app/pages/teams/onlist/components/Disclosures/validation'
import * as snugNotifier from 'app/services/snugNotifier'
import { DEFAULT_MAX_FILE_SIZE_MB } from 'app/utils/file/validation'
import { useFileUpload } from 'app/utils/hooks/useFileUpload'
import { convertYesOrNoToBool } from 'app/utils/onlist/helpers'

function OwnerPropertyDisclosuresForm({
  disclosureRequest,
  addPropertyDisclosureRequestForOwner,
  submitDisclosureInProgress,
}) {
  const [disclosureErrors, setDisclosureErrors] = useState(
    getInitialDisclosureErrors(),
  )

  const [disclosureAnswers, setDisclosureAnswers] = useState({
    isBeingSold: null,
    hasHomicide: null,
    hasEmbeddedNetwork: null,
    compliesWithMinimumStandards: null,
    isRegisteredPlace: null,
    hasAsbestos: null,
    isAffectedWithRelevantAuthorities: null,
    dateOfHomicide: null,
    dateOfRepairNotice: null,
    dateOfGasCheck: null,
    dateOfElectricityCheck: null,
    dateOfPoolCheck: null,
    dateOfDrugUsage: null,
    embeddedNetworkName: '',
    embeddedNetworkAbn: '',
    embeddedNetworkPhone: '',
    embeddedNetworkPricing: '',
    embeddedNetworkWebsite: '',
  })

  // Use shared file upload hook (converts to base64 for API submission)
  const {
    uploadingFiles,
    uploadErrors,
    handleFileUpload: uploadFile,
    handleFileDelete: deleteFile,
  } = useFileUpload({
    maxSizeMB: DEFAULT_MAX_FILE_SIZE_MB,
    convertToBase64: true, // Convert to base64 for API submission
    showNotifications: false, // We'll handle notifications manually
  })

  const validateResponsesAndSetErrors = () => {
    // Use shared validation helper that is version-aware
    const version = getDisclosureLatestVersion()
    const { errors, errorsPresented } = validateDisclosureAnswers(
      disclosureAnswers,
      version,
    )

    // Replace error state completely to clear fixed fields
    // validateDisclosureAnswers returns only fields with errors,
    // so any field not in 'errors' is considered valid
    setDisclosureErrors(errors)

    return errorsPresented
  }

  const onChangeRadioGroup = (field) => {
    return (event) => {
      const { target } = event
      const { id: val } = target

      let errors = { ...disclosureErrors }
      let fieldVal = null

      // if not toggling, then we have a valid bool
      if (disclosureAnswers[field] !== convertYesOrNoToBool(val)) {
        fieldVal = convertYesOrNoToBool(val)
        errors = {
          ...errors,
          [field]: '',
        }
      }

      if (field === 'hasHomicide' && !convertYesOrNoToBool(val)) {
        errors = {
          ...errors,
          dateOfHomicide: '',
        }
      }

      if (field === 'hasEmbeddedNetwork' && !convertYesOrNoToBool(val)) {
        errors = {
          ...errors,
          embeddedNetworkName: '',
          embeddedNetworkAbn: '',
          embeddedNetworkPhone: '',
          embeddedNetworkPricing: '',
          embeddedNetworkWebsite: '',
        }
      }

      if (disclosureAnswers[field] === convertYesOrNoToBool(val)) {
        fieldVal = null
      }

      setDisclosureAnswers({ ...disclosureAnswers, [field]: fieldVal })
      setDisclosureErrors(errors)
    }
  }

  const updateDateInput = (field) => {
    return (data) => {
      let error = data.error
      setDisclosureAnswers({
        ...disclosureAnswers,
        [field]: data.value ? moment(data.value) : data.value,
      })
      setDisclosureErrors({
        ...disclosureErrors,
        [field]: error,
      })
    }
  }

  const inputValueChange = (field) => {
    return (event) => {
      const { value } = event
      if (disclosureAnswers.hasEmbeddedNetwork) {
        if (field === 'embeddedNetworkAbn' && value !== '') {
          setDisclosureErrors((prevState) => ({
            ...prevState,
            embeddedNetworkAbn: '',
          }))
        }
        if (field === 'embeddedNetworkPricing' && value !== '') {
          setDisclosureErrors((prevState) => ({
            ...prevState,
            embeddedNetworkPricing: '',
          }))
        }
        if (field === 'embeddedNetworkName' && value !== '') {
          setDisclosureErrors((prevState) => ({
            ...prevState,
            embeddedNetworkName: '',
          }))
        }
        if (field === 'embeddedNetworkPhone' && value !== '') {
          setDisclosureErrors((prevState) => ({
            ...prevState,
            embeddedNetworkPhone: '',
          }))
        }
        if (field === 'embeddedNetworkWebsite' && value !== '') {
          setDisclosureErrors((prevState) => ({
            ...prevState,
            embeddedNetworkWebsite: 'Please answer this question',
          }))
        }
      }
      if (
        field === 'embeddedNetworkName' ||
        field === 'embeddedNetworkPricing'
      ) {
        setDisclosureAnswers({
          ...disclosureAnswers,
          [field]: value,
        })
        return
      }
      setDisclosureAnswers({
        ...disclosureAnswers,
        [field]: value,
      })

      setDisclosureErrors({
        ...disclosureErrors,
        [field]: event.error,
      })
    }
  }

  // Wrapper for file upload that integrates with disclosure answers state
  const handleFileUpload = (fieldId, file) => {
    uploadFile(fieldId, file, (uploadedFieldId, result) => {
      // Store file metadata and base64 data separately (matching modal pattern)
      setDisclosureAnswers((prev) => ({
        ...prev,
        [uploadedFieldId]: {
          name: result.name,
          pendingUpload: true,
        },
        [`${uploadedFieldId}_data`]: {
          base64: result.base64,
          contentType: result.contentType,
          filename: result.filename,
        },
      }))
      // Clear validation error for this field
      setDisclosureErrors((prev) => ({
        ...prev,
        [uploadedFieldId]: '',
      }))
    })
  }

  // Wrapper for file delete that integrates with disclosure answers state
  const handleFileDelete = (fieldId) => {
    deleteFile(fieldId, (deletedFieldId) => {
      setDisclosureAnswers((prev) => ({
        ...prev,
        [deletedFieldId]: null,
        [`${deletedFieldId}_data`]: null, // Clear base64 data as well
      }))
    })
  }

  const onSubmitDisclosuresClicked = () => {
    const errorsPresent = validateResponsesAndSetErrors()
    if (errorsPresent) {
      snugNotifier.error(
        'There was an error in submission. Please check your responses again',
      )
      return
    }
    if (
      window.confirm(
        'Please ensure that the details entered are correct. These cannot be changed later.',
      )
    ) {
      addPropertyDisclosureRequestForOwner(disclosureAnswers)
    }
  }

  const { property = {} } = disclosureRequest || {}
  const { address = {} } = property || {}
  const propertyAddress = `${address.friendlyName}, ${address.suburb}`

  return (
    <div>
      <Display.CenterContentContainer componentClass=" col-xs-12 summary-box-on-mobile">
        <PropertyDisclosureHeader propertyAddress={propertyAddress} />
        <DisclosureQuestions
          answers={disclosureAnswers}
          disclosureErrors={disclosureErrors}
          onChangeRadioGroup={onChangeRadioGroup}
          updateDateInput={updateDateInput}
          inputValueChange={inputValueChange}
          onFileUpload={handleFileUpload}
          onFileDelete={handleFileDelete}
          uploadingFiles={uploadingFiles}
          uploadErrors={uploadErrors}
          areDisclosuresEditable={true}
          version={getDisclosureLatestVersion()} //if creating a disclosure, use the latest version
        />
        <ButtonWithLoading
          onClick={() => onSubmitDisclosuresClicked()}
          loading={submitDisclosureInProgress}
          disableWhenLoading={true}
        >
          Submit
        </ButtonWithLoading>
      </Display.CenterContentContainer>
    </div>
  )
}

export default OwnerPropertyDisclosuresForm
